import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Loading from '../components/Loading';
import { getMe, setProfile, uploadAvatar, type Me } from '../api/endpoints';
import { useAuthImage } from '../hooks/useAuthImage';
import { navigateTo } from '../utils/navigation';
import Cropper, { type Area } from 'react-easy-crop';
import { getCroppedImageBlob } from '../utils/cropImage';

type GenderOption = '' | 'Female' | 'Male' | 'Prefer not to say' | 'Other';

function meString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function meBirthdateISO(value: unknown): string {
  if (typeof value !== 'string' || value === '0001-01-01T00:00:00Z') return '';
  return value.split('T')[0] ?? '';
}

function toGenderOption(g: string): GenderOption {
  if (g === '' || g === 'Female' || g === 'Male' || g === 'Prefer not to say') return g;
  return 'Other';
}

export default function ProfileCompletionPage(): React.JSX.Element {
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [birthdate, setBirthdate] = useState(''); // YYYY-MM-DD
  const [genderOption, setGenderOption] = useState<GenderOption>('');
  const [genderOther, setGenderOther] = useState('');

  const [avatarPath, setAvatarPath] = useState<string>('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarCropSrc, setAvatarCropSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const params = new URLSearchParams(useLocation().search);
  const fromLogin = params.get('from') === 'login';

  // honor ?next= (same-site only)
  const rawNext = params.get('next');
  const nextSafe = useMemo(() => {
    if (!rawNext) return null;
    const forbidden = ['/login', '/register', '/forgot-password', '/email-verification', '/complete-profile'];
    const isForbidden = forbidden.some((p) => rawNext === p || rawNext.startsWith(p + '?'));
    return rawNext.startsWith('/') && !rawNext.startsWith('//') && !isForbidden ? rawNext : null;
  }, [rawNext]);

  const todayISO = (() => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  })();

  // Field-level validity (all fields are optional server-side; this drives the
  // per-field ticks + the soft "profile looks complete" readiness cue).
  const nameValid = name.trim().length > 0;
  const surnameValid = surname.trim().length > 0;
  const countryValid = country.trim().length > 0;
  const cityValid = city.trim().length > 0;
  const birthdateFuture = birthdate !== '' && birthdate > todayISO;
  const birthdateValid = birthdate !== '' && !birthdateFuture;
  const coreComplete = nameValid && countryValid && birthdateValid;

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    void getMe()
      .then((u: Me) => {
        setName(meString(u.name));
        setSurname(meString(u.surname));
        setCountry(meString(u.country));
        setCity(meString(u.city));
        setAvatarPath(meString(u.avatar));

        setBirthdate(meBirthdateISO(u.birthdate));

        const g = meString(u.gender).trim();
        const option = toGenderOption(g);
        setGenderOption(option);
        setGenderOther(option === 'Other' ? g : '');
      })
      .catch(() => {
        // Backend unreachable / unauthorized — surface it instead of showing an
        // empty form with no feedback (the tester's "nothing happens" case).
        setLoadError(true);
      })
      .finally(() => { setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  // live preview: crop src (while editing) → freshly picked file → server avatar
  const { src: serverAvatar } = useAuthImage(
    avatarPath ? avatarPath.replace(/^\.\/+/, '') : null,
  );

  const avatarPreview = useMemo(() => {
    if (avatarCropSrc) return avatarCropSrc;
    if (avatarFile) return URL.createObjectURL(avatarFile);
    return serverAvatar ?? '';
  }, [avatarCropSrc, avatarFile, serverAvatar]);

  useEffect(() => {
    return () => {
      if (avatarCropSrc) URL.revokeObjectURL(avatarCropSrc);
      if (avatarFile && avatarPreview.startsWith('blob:')) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avatarCropSrc, avatarFile]);

  const handleAvatarPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setAvatarFile(file);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setCroppedAreaPixels(null);
    if (avatarCropSrc) URL.revokeObjectURL(avatarCropSrc);
    setAvatarCropSrc(file ? URL.createObjectURL(file) : null);
  };

  const onCropComplete = (_: Area, area: Area) => { setCroppedAreaPixels(area); };

  const handleAvatarUpload = async () => {
    if (!avatarFile) return;
    setAvatarUploading(true);
    setError('');
    try {
      let blob: Blob = avatarFile;
      if (avatarCropSrc && croppedAreaPixels) {
        blob = await getCroppedImageBlob(avatarCropSrc, croppedAreaPixels);
      }
      await uploadAvatar(blob);
      const fresh = await getMe();
      setAvatarPath(meString(fresh.avatar));
      setAvatarFile(null);
      if (avatarCropSrc) URL.revokeObjectURL(avatarCropSrc);
      setAvatarCropSrc(null);
      setCroppedAreaPixels(null);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
    } catch {
      setError('Could not upload photo');
    } finally {
      setAvatarUploading(false);
    }
  };

  const cancelAvatarEdit = () => {
    setAvatarFile(null);
    if (avatarCropSrc) URL.revokeObjectURL(avatarCropSrc);
    setAvatarCropSrc(null);
    setCroppedAreaPixels(null);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (birthdateFuture) {
      setError('Birthdate can’t be in the future.');
      return;
    }
    setSaving(true);
    try {
      await setProfile({
        name: name.trim(),
        surname: surname.trim(),
        country: country.trim(),
        city: city.trim(),
        birthdate: birthdate ? new Date(birthdate).toISOString() : '0001-01-01T00:00:00Z',
        gender: genderOption === 'Other' ? genderOther.trim() : genderOption,
      });
      setSaved(true);
      navigateTo(nextSafe ?? '/');
    } catch {
      setError('Could not save profile');
      setSaving(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="auth-page">
      {/* Atmospheric orb */}
      <div
        className="orb"
        aria-hidden
        style={{ position: 'absolute', bottom: -80, right: -80, opacity: 0.4 }}
      />

      <div style={{ width: '100%', maxWidth: 480, position: 'relative', zIndex: 1 }}>
        {/* Brand mark */}
        <div className="stack-2" style={{ alignItems: 'center', marginBottom: 'var(--s-5)' }}>
          <span className="logo logo--lg">kivo<span className="dot" /></span>
        </div>

        <div className="card stack-5">
          {/* Header */}
          <div className="stack-2">
            <h1 className="t-h3" style={{ margin: 0 }}>Finish your <em>profile</em></h1>
            <p className="t-small t-mute" style={{ margin: 0 }}>
              A few details are missing. Add them now, or skip and come back later.
            </p>
          </div>

          {/* Backend unreachable — the saved details couldn't load */}
          {loadError && (
            <div
              role="alert"
              className="card card--flat"
              style={{ padding: 'var(--s-3)', border: '1px solid rgba(196,103,58,0.3)', background: 'rgba(196,103,58,0.08)' }}
            >
              <p className="t-small" style={{ margin: 0, color: 'var(--ink)' }}>
                We couldn’t reach the server, so your saved details didn’t load. You can still fill this in and save — or retry.
              </p>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                style={{ marginTop: 'var(--s-2)' }}
                onClick={load}
              >
                Retry
              </button>
            </div>
          )}

          {/* Avatar */}
          <div className="card card--sand card--flat card--bordered" style={{ padding: 'var(--s-4)' }}>
            <div className="row-4">
              <div className="avatar-nav avatar-nav--lg" style={{ flexShrink: 0 }}>
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Profile avatar" />
                ) : (
                  <svg
                    width="22" height="22" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="1.4"
                    strokeLinecap="round" strokeLinejoin="round"
                    aria-hidden style={{ color: 'var(--ink-muted)' }}
                  >
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                  </svg>
                )}
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)', alignItems: 'center' }}>
                <label htmlFor="avatar-input" className="btn btn--ghost btn--sm" style={{ cursor: 'pointer' }}>
                  Choose photo
                </label>
                <input
                  id="avatar-input"
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleAvatarPick}
                />
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  onClick={handleAvatarUpload}
                  disabled={!avatarFile || avatarUploading}
                >
                  {avatarUploading ? 'Uploading…' : 'Save photo'}
                </button>
                {avatarFile && (
                  <button type="button" className="link t-small" onClick={cancelAvatarEdit}>
                    Cancel
                  </button>
                )}
              </div>
            </div>

            <p className="t-small t-mute" style={{ margin: 'var(--s-2) 0 0' }}>
              JPG · PNG · WebP · up to 5 MB
            </p>

            {/* Inline cropper — appears after choosing an image */}
            {avatarCropSrc && (
              <div style={{ marginTop: 'var(--s-4)' }}>
                <style>{`
                  .reactEasyCrop_CropArea { border: 3px solid rgba(42,164,166,.9) !important; }
                  .reactEasyCrop_Container { background: var(--c-charcoal); }
                `}</style>
                <div className="cropper-frame">
                  <Cropper
                    image={avatarCropSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    cropShape="rect"
                    showGrid={false}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                    zoomWithScroll
                    minZoom={1}
                    maxZoom={6}
                    restrictPosition
                  />
                </div>

                <div className="row-3" style={{ justifyContent: 'center', marginTop: 'var(--s-3)' }}>
                  <span className="t-small t-mute">Zoom</span>
                  <input
                    type="range"
                    min={1}
                    max={6}
                    step={0.01}
                    value={zoom}
                    onChange={(e) => { setZoom(Number.parseFloat(e.target.value)); }}
                    style={{ width: 'min(60vw, 240px)', accentColor: 'var(--c-kivo-teal)' }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="stack-4" noValidate>
            <div className="stack-3">
              <TickedField valid={nameValid}>
                <input
                  className="form-input"
                  placeholder="First name"
                  value={name}
                  onChange={(e) => { setName(e.target.value); }}
                  style={{ paddingRight: 'var(--s-7)' }}
                />
              </TickedField>
              <TickedField valid={surnameValid}>
                <input
                  className="form-input"
                  placeholder="Surname"
                  value={surname}
                  onChange={(e) => { setSurname(e.target.value); }}
                  style={{ paddingRight: 'var(--s-7)' }}
                />
              </TickedField>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--s-3)' }}>
                <TickedField valid={countryValid}>
                  <input
                    className="form-input"
                    placeholder="Country"
                    value={country}
                    onChange={(e) => { setCountry(e.target.value); }}
                    style={{ paddingRight: 'var(--s-7)' }}
                  />
                </TickedField>
                <TickedField valid={cityValid}>
                  <input
                    className="form-input"
                    placeholder="City"
                    value={city}
                    onChange={(e) => { setCity(e.target.value); }}
                    style={{ paddingRight: 'var(--s-7)' }}
                  />
                </TickedField>
              </div>

              <div className="stack-2">
                <label htmlFor="birthdate" className="t-small t-mute" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--s-1)' }}>
                  Birthdate {birthdateValid && <InlineCheck />}
                </label>
                <input
                  id="birthdate"
                  type="date"
                  value={birthdate}
                  onChange={(e) => { setBirthdate(e.target.value); }}
                  max={todayISO}
                  className="form-input"
                  aria-invalid={birthdateFuture}
                />
                {birthdateFuture && (
                  <p className="field-error" style={{ margin: 0 }}>Birthdate can’t be in the future.</p>
                )}
              </div>

              <div className="stack-2">
                <label htmlFor="gender-select" className="t-small t-mute">Gender</label>
                <div style={{ position: 'relative' }}>
                  <select
                    id="gender-select"
                    value={genderOption}
                    onChange={(e) => {
                      const v = e.target.value as GenderOption;
                      setGenderOption(v);
                      if (v !== 'Other') setGenderOther('');
                    }}
                    className="form-input"
                    style={{
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      paddingRight: 'var(--s-7)',
                      color: genderOption ? 'var(--ink)' : 'var(--ink-muted)',
                    }}
                    aria-label="Select your gender"
                  >
                    <option value="">— Select —</option>
                    <option value="Female">Female</option>
                    <option value="Male">Male</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                    <option value="Other">Other</option>
                  </select>
                  <svg
                    style={{
                      pointerEvents: 'none',
                      position: 'absolute',
                      right: 'var(--s-4)',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 16,
                      height: 16,
                      color: 'var(--ink-muted)',
                    }}
                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </div>

                {genderOption === 'Other' && (
                  <input
                    className="form-input"
                    placeholder="Enter gender"
                    value={genderOther}
                    onChange={(e) => { setGenderOther(e.target.value); }}
                  />
                )}
              </div>
            </div>

            {error && <p className="field-error" style={{ margin: 0 }}>{error}</p>}

            {/* Soft readiness cue — none of this is required, it's reassurance */}
            {coreComplete && !error && (
              <div
                className="row-2"
                style={{ alignItems: 'center', color: 'var(--c-kivo-teal)' }}
              >
                <InlineCheck />
                <span className="t-small" style={{ fontWeight: 500 }}>Profile looks complete</span>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 'var(--s-2)' }}>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => { navigateTo('/'); }}
              >
                Skip for now
              </button>
              <button type="submit" className="btn btn--primary" disabled={saving || saved}>
                {saved ? 'Saved ✓' : saving ? 'Saving…' : 'Save & continue'}
              </button>
            </div>

            <p className="t-small t-mute" style={{ margin: 0 }}>
              You can update these anytime in{' '}
              <button type="button" className="link" onClick={() => { navigateTo('/profile'); }}>
                Profile
              </button>.
              {fromLogin && nextSafe && (
                <span style={{ display: 'block', marginTop: 'var(--s-1)' }}>
                  After saving, you&apos;ll be taken to{' '}
                  <code style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{nextSafe}</code>.
                </span>
              )}
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

// Small teal check used for per-field ticks and the readiness cue.
function InlineCheck(): React.JSX.Element {
  return (
    <svg
      aria-hidden
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ color: 'var(--c-kivo-teal)' }}
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

// Wraps a text input and overlays a trailing tick once the value is valid.
function TickedField({ valid, children }: { valid: boolean; children: React.ReactNode }): React.JSX.Element {
  return (
    <div style={{ position: 'relative' }}>
      {children}
      {valid && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            right: 'var(--s-3)',
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'inline-flex',
            pointerEvents: 'none',
          }}
        >
          <InlineCheck />
        </span>
      )}
    </div>
  );
}
